import React, { useState } from "react";
import {
  FaTimes,
  FaRegFilePdf,
  FaRegImage,
  FaFileAlt,
  FaEye,
  FaDownload,
} from "react-icons/fa";

function NoteDetailsModal({ note, onClose }) {
  const [previewFile, setPreviewFile] = useState(null);

  if (!note) return null;

  const rawAttachments = Array.isArray(note.attachments)
    ? note.attachments
    : [];

  const getFileIcon = (file) => {
    const ext = (
      file.fileType ||
      file.fileName?.split(".").pop() ||
      ""
    ).toLowerCase();
    if (ext === "pdf") {
      return (
        <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center text-red-500">
          <FaRegFilePdf className="text-[18px]" />
        </div>
      );
    }
    if (["jpg", "jpeg", "png", "webp", "gif"].includes(ext)) {
      return (
        <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center text-green-500">
          <FaRegImage className="text-[18px]" />
        </div>
      );
    }
    return (
      <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center text-blue-500">
        <FaFileAlt className="text-[18px]" />
      </div>
    );
  };

  const getFileUrl = (fileName) => {
    const baseUrl =
      process.env.REACT_APP_IMAGE_URL ||
      "https://shadow-nonprod-application-data.s3.us-east-2.amazonaws.com/";
    return `${baseUrl}${fileName}`;
  };

  const handleDownload = (file) => {
    const url = getFileUrl(file.fileName);
    window.open(url, "_blank");
  };

  return (
    <>
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
        onClick={onClose}
      >
        <div
          className="w-full max-w-2xl bg-white rounded-[22px] overflow-hidden shadow-xl max-h-[90vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-6 py-5 flex items-center justify-between border-b border-[#edf1f7]">
            <h2 className="text-[18px] font-bold text-[#111827]">
              Clinical Note Details
            </h2>
            <button
              onClick={onClose}
              className="text-[#6b7280] cursor-pointer bg-white hover:text-[#111827]"
            >
              <FaTimes className="text-[16px]" />
            </button>
          </div>

          <div className="p-6 overflow-y-auto">
            {/* Note Information */}
            <h3 className="text-[13px] font-bold text-[#008f9f] mb-3">
              Note Information
            </h3>
            <div className="border border-[#edf1f7] rounded-[14px] p-5 grid grid-cols-2 gap-y-4 mb-6">
              <div>
                <p className="text-[11px] text-[#6b7280] mb-1">
                  Status / Priority
                </p>
                <span className="inline-block px-3 py-1 rounded-md text-[10px] font-bold text-purple-700 bg-purple-100">
                  {note.status || note.priority || "CLOSED"}
                </span>
              </div>
              <div>
                <p className="text-[11px] text-[#6b7280] mb-1">Date & Time</p>
                <p className="text-[13px] font-bold text-[#111827]">
                  {note.noteDatetime || note.datetime || note.createdDatetime}
                </p>
              </div>
              <div>
                <p className="text-[11px] text-[#6b7280] mb-1">Recorded By</p>
                <p className="text-[13px] font-bold text-[#111827]">
                  {note.recordedByName || note.recordedBy || note.createdBy}
                </p>
              </div>
              <div>
                <p className="text-[11px] text-[#6b7280] mb-1">Attachments</p>
                <p className="text-[13px] font-bold text-[#111827]">
                  {rawAttachments.length} Files
                </p>
              </div>
            </div>

            {/* Note Content */}
            <h3 className="text-[13px] font-bold text-[#008f9f] mb-3">
              Note Content
            </h3>
            <div className="border border-[#edf1f7] rounded-[14px] p-5 mb-6">
              <p className="text-[13px] text-[#374151] leading-relaxed whitespace-pre-line">
                {note.noteText || note.fullContent || note.content}
              </p>
            </div>

            {/* Attachments */}
            <h3 className="text-[13px] font-bold text-[#008f9f] mb-3">
              Attachments ({rawAttachments.length})
            </h3>
            <div className="space-y-3 mb-4">
              {rawAttachments.length === 0 ? (
                <p className="text-xs text-gray-400 italic">
                  No attachments uploaded for this note.
                </p>
              ) : (
                rawAttachments.map((file, idx) => (
                  <div
                    key={file.attachmentId || idx}
                    className="flex items-center justify-between border border-[#edf1f7] rounded-[12px] p-3 hover:bg-gray-50 transition"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      {getFileIcon(file)}
                      <div className="truncate">
                        <p className="text-[13px] font-bold text-[#111827] truncate">
                          {file.fileName || `Attachment_${idx + 1}`}
                        </p>
                        <p className="text-[11px] text-[#6b7280]">
                          {(file.fileType || "file").toUpperCase()}{" "}
                          {file.fileSizeKb ? `• ${file.fileSizeKb} KB` : ""}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setPreviewFile(file)}
                        title="View File"
                        className="w-8 h-8 flex items-center bg-white cursor-pointer justify-center rounded-md border border-[#edf1f7] bg-white text-[#008f9f] hover:bg-teal-50"
                      >
                        <FaEye />
                      </button>
                      {/* <button
                        onClick={() => handleDownload(file)}
                        title="Download / Open in New Tab"
                        className="w-8 h-8 flex items-center bg-white cursor-pointer justify-center rounded-md border border-[#edf1f7] bg-white text-[#008f9f] hover:bg-teal-50"
                      >
                        <FaDownload />
                      </button> */}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* File Preview Modal */}
      {previewFile && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 px-4"
          onClick={() => setPreviewFile(null)}
        >
          <div
            className="w-full max-w-4xl bg-white rounded-[22px] overflow-hidden shadow-2xl max-h-[92vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 flex items-center justify-between border-b border-[#edf1f7]">
              <h3 className="text-[16px] font-bold text-[#111827] truncate">
                {previewFile.fileName}
              </h3>
              <div className="flex items-center gap-3">
                {/* <button
                  onClick={() => handleDownload(previewFile)}
                  className="px-3 py-1.5 rounded-lg border border-[#edf1f7] text-[#008f9f] text-xs font-bold flex items-center gap-1.5 hover:bg-teal-50"
                >
                  <FaDownload /> Open in New Tab
                </button> */}
                <button
                  onClick={() => setPreviewFile(null)}
                  className="text-[#6b7280] bg-white hover:text-[#111827]"
                >
                  <FaTimes className="text-[16px]" />
                </button>
              </div>
            </div>

            <div className="p-4 flex-1 overflow-auto flex items-center justify-center bg-[#f8fafc] min-h-[400px]">
              {previewFile.fileName?.match(/\.(jpg|jpeg|png|webp|gif)$/i) ||
              ["jpg", "jpeg", "png", "webp", "gif"].includes(
                previewFile.fileType?.toLowerCase(),
              ) ? (
                <img
                  src={getFileUrl(previewFile.fileName)}
                  alt={previewFile.fileName}
                  className="max-h-[70vh] object-contain rounded-lg shadow-sm"
                />
              ) : (
                <iframe
                  src={getFileUrl(previewFile.fileName)}
                  title={previewFile.fileName}
                  className="w-full h-[70vh] rounded-lg border border-[#edf1f7]"
                />
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default NoteDetailsModal;
